import React from "react";

import { DETAIL_LIST, TONE } from "../constants";
import TagChip from "./TagChip";

// How a version reaches a tenant_id for one table. The word is the API's own
// `scoping` value, so a category added on the server shows up here as itself
// rather than as a blank chip.
const SCOPING = {
    direct: {
        tone: TONE.emerald,
        hint: "Has its own tenant_id column — policies compare it directly.",
    },
    leaf: {
        tone: TONE.violet,
        hint: "No tenant_id of its own — policies join up to a parent that has one.",
    },
    "platform-resolvable": {
        tone: TONE.amber,
        hint:
            "Writes are tenant-scoped, but a read is allowed with no tenant " +
            "context at all: the row is what tells us which tenant the caller " +
            "belongs to.",
    },
};

/**
 * The tables a version polices, each with how it was scoped and — for leaf
 * tables — the join path its policies walk to reach a tenant_id.
 *
 * The path is the part worth showing. "outreach_messages is a leaf table" says
 * almost nothing; "outreach_messages.sourcing_candidate_id →
 * sourcing_candidates.campaign_id → campaigns.created_by → users.tenant_id"
 * says exactly why a row is visible, and it is the first thing to check when a
 * tenant reports seeing too much or too little.
 */
const ScopedTableList = ({ tables }) => {
    if (!tables?.length) return null;
    return (
        <div className={DETAIL_LIST}>
            {tables.map((table) => {
                const scoping = SCOPING[table.scoping];
                return (
                    <div key={table.name} className="px-3 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-[12px] text-text-primary">
                                {table.name}
                            </span>
                            <TagChip
                                tone={scoping?.tone ?? TONE.slate}
                                label={table.scoping}
                                title={scoping?.hint}
                            />
                        </div>
                        {table.chain && (
                            <div
                                className="text-[11px] text-text-secondary mt-1 font-mono break-words"
                                title="The join path this table's policies walk to reach a tenant_id"
                            >
                                {table.chain}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ScopedTableList;
